import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilePdf, FaCheckSquare, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FiCalendar, FiBox, FiUser, FiFileText, FiHash, FiDollarSign, FiSearch, FiArrowLeft, FiPieChart, FiTrendingUp } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from '../../../../img/logo rio verde pdf.png';
import styles from './DespesasGerais.module.css';
import { API_BASE_URL } from "../../../../config";
import { handleGeneratePDF as generateExpensesPDF } from './reportGeneratorDespesasGerais';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../../components/LoadingOverlay/LoadingOverlay';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ExpensesPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expenses, setExpenses] = useState([]);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;
    const [pageLoading, setPageLoading] = useState(true);
    const navigate = useNavigate();

    const [lineChartData, setLineChartData] = useState(null);
    const [doughnutChartData, setDoughnutChartData] = useState(null);
    const [activeMobileChart, setActiveMobileChart] = useState('doughnut');

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();

        const fetchExpenses = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error("Usuário não autenticado. Redirecionando para login.");
                navigate('/');
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/despesas`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (response.status === 401) {
                    console.error("Token inválido ou expirado.");
                    navigate('/');
                    return;
                }
                if (!response.ok) {
                    throw new Error('Falha ao buscar despesas');
                }
                const data = await response.json();
                setExpenses(data);
            } catch (error) {
                console.error("Erro ao buscar despesas:", error);
            } finally {
                const endTime = Date.now();
                const duration = endTime - startTime;
                const remainingTime = MIN_LOADING_TIME - duration;

                if (remainingTime > 0) {
                    setTimeout(() => setPageLoading(false), remainingTime);
                } else {
                    setPageLoading(false);
                }
            }
        };

        fetchExpenses();
    }, [navigate]);

    useEffect(() => {
        if (expenses.length > 0) {
            processChartData(expenses);
        }
    }, [expenses]);

    const processChartData = (data) => {
        const monthlyExpenses = data.reduce((acc, expense) => {
            try {
                const date = new Date(expense.data);
                if (isNaN(date.getTime())) {
                    console.warn("Data inválida:", expense.data);
                    return acc;
                }
                const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                const value = parseFloat(expense.total) || 0;
                
                if (!acc[monthYear]) {
                    acc[monthYear] = 0;
                }
                acc[monthYear] += value;
            } catch (e) {
                console.warn("Erro processando data:", expense.data, e);
            }
            return acc;
        }, {});

        const sortedMonths = Object.keys(monthlyExpenses).sort();
        const lineLabels = sortedMonths.map(monthYear => {
            const [year, month] = monthYear.split('-');
            return `${month}/${year.slice(2)}`;
        });
        const lineValues = sortedMonths.map(month => monthlyExpenses[month]);

        setLineChartData({
            labels: lineLabels,
            datasets: [
                {
                    label: 'Total Gasto por Mês',
                    data: lineValues,
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#28a745',
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#28a745',
                    pointHoverBorderColor: '#fff',
                },
            ],
        });

        const categoryExpenses = data.reduce((acc, expense) => {
            const category = expense.tipo_de_despesa || 'Não Categorizado';
            const value = parseFloat(expense.total) || 0;

            if (!acc[category]) {
                acc[category] = 0;
            }
            acc[category] += value;
            return acc;
        }, {});

        const doughnutLabels = Object.keys(categoryExpenses);
        const doughnutValues = Object.values(categoryExpenses);

        const dynamicColors = doughnutLabels.map((_, index) => {
            const hue = (index * 137.508) % 360;
            return `hsl(${hue}, 70%, 50%)`;
        });


        setDoughnutChartData({
            labels: doughnutLabels,
            datasets: [
                {
                    label: 'Despesas por Categoria',
                    data: doughnutValues,
                    backgroundColor: dynamicColors,
                    borderColor: '#ffffff',
                    borderWidth: 2,
                    hoverOffset: 8,
                },
            ],
        });
    };

    const formatCurrency = (value) => {
        const numericValue = Number(value) || 0;
        return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Data inválida';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Data inválida';
            }
            return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
        } catch (error) {
            console.error("Erro ao formatar data:", dateString, error);
            return 'Data inválida';
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleSelectExpense = (id) => {
        setSelectedExpenses(prevSelected =>
            prevSelected.includes(id)
                ? prevSelected.filter(expId => expId !== id)
                : [...prevSelected, id]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedExpenses(filteredExpenses.map(exp => exp.id));
        } else {
            setSelectedExpenses([]);
        }
    };

    const filteredExpenses = expenses.filter(expense => {
        const searchTermLower = searchTerm.toLowerCase();
        return (
            (expense.imovel_nome && expense.imovel_nome.toLowerCase().includes(searchTermLower)) ||
            (expense.descricao && expense.descricao.toLowerCase().includes(searchTermLower)) ||
            (expense.fornecedor && expense.fornecedor.toLowerCase().includes(searchTermLower)) ||
            (expense.numero_nota_fiscal && expense.numero_nota_fiscal.toLowerCase().includes(searchTermLower)) ||
            (expense.tipo_de_despesa && expense.tipo_de_despesa.toLowerCase().includes(searchTermLower))
        );
    });

    const totalPages = Math.ceil(filteredExpenses.length / ITEMS_PER_PAGE);
    const paginatedExpenses = filteredExpenses.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const paginate = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleGenerateSelectedPDF = () => {
        const expensesToExport = selectedExpenses.length > 0
            ? expenses.filter(exp => selectedExpenses.includes(exp.id))
            : filteredExpenses;

        if (expensesToExport.length === 0) {
            alert("Nenhuma despesa para gerar o PDF. Selecione despesas ou filtre a lista.");
            return;
        }

        generateExpensesPDF({
            selectedExpenses: expensesToExport,
            formatCurrency,
            formatDate,
            logo: logo
        });
    };
    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                position: 'top',
                labels: {
                    color: '#495057',
                    font: {
                        family: "'Inter', sans-serif",
                        size: 13,
                    },
                    boxWidth: 20,
                    padding: 20,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(33,37,41,0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#6c757d',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                titleFont: {
                    size: 14,
                    weight: 'bold',
                },
                bodyFont: {
                    size: 13,
                },
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatCurrency(context.parsed.y);
                        }
                        return label;
                    },
                    title: function(context) {
                        return context[0].label;
                    }
                }
            }
        },
        scales: {
            y: {
                ticks: {
                    callback: function(value) {
                        return formatCurrency(value);
                    },
                    color: '#6c757d',
                    font: {
                        family: "'Inter', sans-serif",
                        size: 12,
                    },
                },
                grid: {
                    color: '#e9ecef',
                    borderDash: [5, 5],
                },
                border: {
                    display: false,
                }
            },
            x: {
                ticks: {
                    color: '#6c757d',
                    font: {
                        family: "'Inter', sans-serif",
                        size: 12,
                    },
                },
                grid: {
                    display: false
                },
                border: {
                    display: false,
                }
            }
        }
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                position: 'bottom',
                labels: {
                    color: '#495057',
                    font: {
                        family: "'Inter', sans-serif",
                        size: 12,
                    },
                    boxWidth: 18,
                    padding: 18
                }
            },
            tooltip: {
                backgroundColor: 'rgba(33,37,41,0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#6c757d',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
                titleFont: {
                    size: 14,
                    weight: 'bold',
                },
                bodyFont: {
                    size: 13,
                },
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = (context.raw / total) * 100;
                            label += formatCurrency(context.parsed) + ` (${percentage.toFixed(1)}%)`;
                        }
                        return label;
                    },
                    title: function(context) {
                        return context[0].label;
                    }
                }
            },
            title: {
                display: true,
                text: 'Despesas por Categoria',
                font: {
                    size: 18,
                    family: "'Inter', sans-serif",
                    weight: '700'
                },
                color: '#212529',
                padding: {
                    bottom: 25,
                    top: 10
                }
            }
        },
        cutout: '65%',
    };
    
    const lineChartTitle = {
        ...chartOptions,
        plugins: {
            ...chartOptions.plugins,
            title: {
                display: true,
                text: 'Total Gasto por Mês',
                font: {
                    size: 18,
                    family: "'Inter', sans-serif",
                    weight: '700'
                },
                color: '#212529',
                padding: {
                    bottom: 25,
                    top: 10
                }
            }
        }
    };


    return (
        <div className={styles.expensesPageContainer}>
            {pageLoading && <LoadingOverlay text="Carregando despesas..." />}

            {!pageLoading && (
                <>
                    <PageHeader
                        title="Despesas Gerais"
                        subtitle="Consulte e gerencie todas as despesas da sua operação."
                        icon={<FiDollarSign size={28} />}
                        backTo="/sistema-imoveis"
                    />

                    {(lineChartData && doughnutChartData) && (
                        <div className={styles.chartsContainer}>
                            <div className={styles.chartSwitcher}>
                                <button
                                    className={`${styles.switchButton} ${activeMobileChart === 'doughnut' ? styles.active : ''}`}
                                    onClick={() => setActiveMobileChart('doughnut')}
                                >
                                    <FiPieChart />
                                    Categorias
                                </button>
                                <button
                                    className={`${styles.switchButton} ${activeMobileChart === 'line' ? styles.active : ''}`}
                                    onClick={() => setActiveMobileChart('line')}
                                >
                                    <FiTrendingUp />
                                    Evolução
                                </button>
                            </div>

                            <div className={`${styles.chartBox} ${styles.lineChart} ${activeMobileChart === 'line' ? styles.showOnMobile : ''}`}>
                                {lineChartData.labels.length > 0 ? (
                                    <div className={styles.chartWrapper}>
                                        <Line options={lineChartTitle} data={lineChartData} />
                                    </div>
                                ) : (
                                    <div className={styles.noChartData}>
                                        <p>Não há dados suficientes para o gráfico de linha.</p>
                                    </div>
                                )}
                            </div>
                            <div className={`${styles.chartBox} ${styles.doughnutChart} ${activeMobileChart === 'doughnut' ? styles.showOnMobile : ''}`}>
                                {doughnutChartData.labels.length > 0 ? (
                                    <div className={styles.chartWrapper}>
                                        <Doughnut options={doughnutOptions} data={doughnutChartData} />
                                    </div>
                                ) : (
                                    <div className={styles.noChartData}>
                                        <p>Não há dados suficientes para o gráfico de categorias.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={styles.controlsAndTableContainer}>
                        <div className={styles.controlsHeader}>
                            <div className={styles.searchContainer}>
                                <FiSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por imóvel, fornecedor, descrição, NF-e ou tipo..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className={styles.searchInput}
                                />
                            </div>
                            <button onClick={handleGenerateSelectedPDF} className={styles.pdfButton}>
                                <FaFilePdf />
                                Gerar PDF
                            </button>
                        </div>

                        <table className={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            type="checkbox"
                                            onChange={handleSelectAll}
                                            checked={selectedExpenses.length === filteredExpenses.length && filteredExpenses.length > 0}
                                            className={styles.checkbox}
                                            aria-label="Selecionar todas as despesas"
                                        />
                                    </th>
                                    <th>Data</th>
                                    <th>Imóvel</th>
                                    <th>Fornecedor</th>
                                    <th>Descrição</th>
                                    <th>Qtd.</th>
                                    <th>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedExpenses.length > 0 ? (
                                    paginatedExpenses.map(expense => (
                                        <tr key={expense.id}>
                                            <td data-label="Selecionar">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedExpenses.includes(expense.id)}
                                                    onChange={() => handleSelectExpense(expense.id)}
                                                    className={styles.checkbox}
                                                    aria-label={`Selecionar despesa ${expense.id}`}
                                                />
                                            </td>
                                            <td data-label="Data">{formatDate(expense.data)}</td>
                                            <td data-label="Imóvel">{expense.descricao_imovel || 'N/A'}</td>
                                            <td data-label="Fornecedor">{expense.fornecedor}</td>
                                            <td data-label="Descrição">{expense.descricao}</td>
                                            <td data-label="Qtd." style={{ textAlign: 'center' }}>{expense.quantidade}</td>
                                            <td data-label="Valor" style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(expense.total)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className={styles.noResults}>
                                            <div className={styles.noResultsContent}>
                                                <FiSearch size={40} />
                                                <h4>Nenhum resultado encontrado</h4>
                                                <p>Tente ajustar os termos da sua pesquisa.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <button 
                                    onClick={() => paginate(currentPage - 1)} 
                                    disabled={currentPage === 1} 
                                    className={styles.paginationArrow}
                                    aria-label="Página anterior"
                                >
                                    <FaChevronLeft />
                                </button>
                                <span className={styles.paginationInfo}>
                                    Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                                </span>
                                <button 
                                    onClick={() => paginate(currentPage + 1)} 
                                    disabled={currentPage === totalPages} 
                                    className={styles.paginationArrow}
                                    aria-label="Próxima página"
                                >
                                    <FaChevronRight />
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ExpensesPage;